import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';

import {
  getJobTimings,
  type JobKindTiming,
  type JobTimings,
} from '../api/admin';
import { extractErrorMessage } from '../api/errors';
import { useSortedRows } from '../util/tableSort';
import { RefreshButton } from './RefreshButton';
import { SearchableSelect } from './SearchableSelect';
import { SortHeader } from './SortHeader';

/**
 * How long the pipeline takes, against how much data it read (JOB-TIMINGS).
 *
 * Scoring cost should track recording size. Plotting the two together turns
 * "it feels slower" into something checkable: a regression shows up as a
 * slope change rather than as an impression. That distinction is not
 * academic — an "EEG processing is very slow" report once turned out to be a
 * job failing and retrying, which this view would have shown immediately as
 * no completed points at all.
 *
 * The kinds are deliberately coloured apart because they behave differently:
 * cognitive and motion scale with the recording, while marker_labels is
 * LLM- and audio-bound and has essentially no size relationship.
 */

const KIND_COLORS: Record<string, string> = {
  cognitive: '#1e3a8a',
  motion: '#7c3aed',
  marker_labels: '#b45309',
  compress: '#0f766e',
  audio_sync: '#be123c',
  thumbnails: '#4d7c0f',
};

const FALLBACK_COLOR = '#64748b';

// Time windows have a natural order (shortest first) — kept, not alphabetized.
const WINDOW_OPTIONS = [
  { value: '1', label: '24 hours' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
];

const NO_KINDS: JobKindTiming[] = [];

const KIND_ACCESSORS = {
  kind: (k: JobKindTiming) => k.kind,
  count: (k: JobKindTiming) => k.count,
  median: (k: JobKindTiming) => k.median_seconds,
  p95: (k: JobKindTiming) => k.p95_seconds,
  max: (k: JobKindTiming) => k.max_seconds,
  per100k: (k: JobKindTiming) => k.seconds_per_100k_records,
};

/** Seconds → "1m 04s" past a minute; plain seconds below that. */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds - m * 60);
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

/** Records → "1.9M" / "141k", because raw seven-digit counts don't read. */
export function formatRecords(records: number): string {
  if (records >= 1_000_000) return `${(records / 1_000_000).toFixed(1)}M`;
  if (records >= 1_000) return `${Math.round(records / 1_000)}k`;
  return String(records);
}

export function JobTimingsCard() {
  const theme = useTheme();
  const [days, setDays] = useState(7);
  const [data, setData] = useState<JobTimings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getJobTimings(days));
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  // Default: by kind, the order the API returns them in.
  const { sort, sorted: sortedKinds } = useSortedRows(
    data?.kinds ?? NO_KINDS,
    KIND_ACCESSORS,
    'kind',
  );

  // Only jobs whose recording has a record count can be plotted against size;
  // the rest still count in the table, where duration alone is the point.
  const plottable = (data?.points ?? []).filter(
    (p) => p.records !== null && p.records > 0,
  );
  const kindsInPlot = [...new Set(plottable.map((p) => p.kind))];

  return (
    <Card>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              Scoring throughput
            </Typography>
            <Typography variant="body2" color="text.secondary">
              How long each job takes against how much data it read. Cognitive
              and motion should track recording size; marker_labels is
              LLM-bound and will not.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchableSelect
              size="small"
              value={String(days)}
              onChange={(v) => setDays(Number(v))}
              sx={{ width: 120 }}
              label="Window"
              options={WINDOW_OPTIONS}
              keepOrder
            />
            <RefreshButton onClick={() => void load()} refreshing={loading} />
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {data === null ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : data.points.length === 0 ? (
          <Alert severity="info" variant="outlined">
            No jobs completed in this window. If recordings are being uploaded,
            that is itself the finding — check the queue and the worker
            heartbeats above.
          </Alert>
        ) : (
          <>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <SortHeader label="Job" col="kind" sort={sort} />
                  <SortHeader label="Runs" col="count" sort={sort} align="right" />
                  <SortHeader label="Median" col="median" sort={sort} align="right" />
                  <SortHeader label="p95" col="p95" sort={sort} align="right" />
                  <SortHeader label="Slowest" col="max" sort={sort} align="right" />
                  <SortHeader
                    label="Per 100k records"
                    col="per100k"
                    sort={sort}
                    align="right"
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedKinds.map((k) => (
                  <TableRow key={k.kind}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: KIND_COLORS[k.kind] ?? FALLBACK_COLOR,
                          }}
                        />
                        <span>{k.kind}</span>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{k.count}</TableCell>
                    <TableCell align="right">
                      {formatDuration(k.median_seconds)}
                    </TableCell>
                    <TableCell align="right">
                      {formatDuration(k.p95_seconds)}
                    </TableCell>
                    <TableCell align="right">
                      {formatDuration(k.max_seconds)}
                    </TableCell>
                    <TableCell align="right">
                      {k.seconds_per_100k_records === null
                        ? '—'
                        : formatDuration(k.seconds_per_100k_records)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {plottable.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No completed job in this window has a recording with a record
                count yet — the counts start with recordings captured after
                the durability release, so the plot fills in from here.
              </Typography>
            ) : (
              <Box sx={{ height: 300 }} data-testid="job-timings-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart
                    margin={{ top: 8, right: 16, bottom: 24, left: 8 }}
                  >
                    <CartesianGrid
                      stroke={theme.palette.divider}
                      strokeDasharray="3 3"
                    />
                    <XAxis
                      type="number"
                      dataKey="records"
                      name="Records"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v: number) => formatRecords(v)}
                      label={{
                        value: 'Records in the recording',
                        position: 'insideBottom',
                        offset: -12,
                        style: { fontSize: 11 },
                      }}
                    />
                    <YAxis
                      type="number"
                      dataKey="seconds"
                      name="Seconds"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v: number) => formatDuration(v)}
                    />
                    <ZAxis range={[45, 45]} />
                    <Tooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      formatter={(value, name) => {
                        const n = typeof value === 'number' ? value : 0;
                        return String(name) === 'Seconds'
                          ? [formatDuration(n), 'Took']
                          : [formatRecords(n), 'Records'];
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {kindsInPlot.map((kind) => (
                      <Scatter
                        key={kind}
                        name={kind}
                        data={plottable.filter((p) => p.kind === kind)}
                        fill={KIND_COLORS[kind] ?? FALLBACK_COLOR}
                        isAnimationActive={false}
                      />
                    ))}
                  </ScatterChart>
                </ResponsiveContainer>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
